<script setup lang="ts">
  import {
    ChevronDown as ChevronDownIcon,
    LoaderCircle as LoaderCircleIcon,
    X as XIcon,
  } from 'lucide-vue-next'
  import type { SelectHTMLAttributes } from 'vue'
  import { computed, ref } from 'vue'
  import { randomId } from '@/functions'

  const modelValue = defineModel<SelectHTMLAttributes['value']>()

  const props = defineProps<{
    id?: string,
    icon?: string,
    disabled?: boolean,
    label?: string,
    required?: boolean,
    error?: string | string[],
    placeholder?: string,
    loading?: boolean,
  }>()

  const focus = ref(false)

  const defaultId = randomId()
  const inputId = computed(() => props.id ?? defaultId)
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-red-600">
        *
      </span>
    </label>
    <div
      class="relative flex items-center border rounded"
      :class="{ 'input-ring': focus }">
      <select
        :id="inputId"
        v-model="modelValue"
        :required="required"
        :disabled="disabled"
        @focus="focus = true"
        @blur="focus = false">
        <option
          v-if="placeholder"
          :value="null"
          hidden>
          {{ placeholder }}
        </option>
        <slot></slot>
      </select>

      <div
        class="absolute right-2 z-10 flex items-center text-xs text-gray-500"
        :class="{
          'pointer-events-none': !modelValue
        }">
        <loader-circle-icon
          v-if="loading"
          class="animate-spin size-3" />
        <x-icon
          v-else-if="modelValue"
          class="cursor-pointer size-3 text-red-500"
          @click="modelValue = null" />
        <chevron-down-icon
          v-else
          class="size-3" />
      </div>

      <app-error-msg :message="error" />
    </div>
  </div>
</template>

<style scoped>
  @reference '@/styles/main.css';

  /* border on the container div, so that the focus ring enclose the icon as well */
  /* then the select height should less than h-9 */
  select {
    @apply pl-2 capitalize h-8.5 block flex-1 cursor-pointer appearance-none rounded pr-7 text-sm text-gray-700 focus-visible:outline-primary focus-visible:ring-0;
  }
</style>
